import React, {
  createContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';

type User = {
  id: string;
  email: string;
  userName: string;
};

export type AuthContextType = {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
};

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  login: (token: string, user: User) => null,
  logout: () => null
};

const AuthContext = createContext<AuthContextType>({
  ...initialState,
});

export const AuthContextProvider = ({ children }: { children: ReactNode; }) => {
  const [token, setToken] = useState<string | null>(initialState.token);
  const [user, setUser] = useState<User | null>(initialState.user);
  const [isLoading, setIsLoading] = useState<boolean>(initialState.isLoading);

  const login = (
    newToken: string,
    newUser: User
  ) => {
    if(newToken) {
      localStorage.setItem(
        'authToken',
        newToken
      );
    }

    if(newUser) {
      localStorage.setItem(
        'authUser',
        JSON.stringify(newUser)
      );
    }

    setToken(newToken);
    setUser(newUser);
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');

    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    const handleLogout = () => {
      logout();
    };

    window.addEventListener("logout", handleLogout);

    return () => {
      window.removeEventListener("logout", handleLogout);
    };
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');

    if (storedToken) {
      setToken(storedToken);
    }

    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    setIsLoading(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
